import React, {useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {useNavigate} from "react-router-dom";

import {StatCard} from "@/components/dashboard/StatCard";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";

import {
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";

import {
    DollarSign,
    Users,
    AlertTriangle,
    Calendar,
    TrendingUp,
    Clock,
    Target,
    Percent,
    Wallet,
} from "lucide-react";

import {usePeople} from "@/context/PeopleContext";
import {useClients} from "@/hooks/useClient";
import {formatNumber} from "@/utils/helpers";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import type {PersonModelInterface, Payment} from "@/models/personModel"; // поправь путь под себя

type StatusKey = "new" | "pending" | "overdue" | "closed" | "closedEarly" | "returned" | "unknown";

const STATUS_META: { key: StatusKey; label: string; tone: "ok" | "warn" | "bad" | "neutral" }[] = [
    {key: "new", label: "Новые", tone: "neutral"},
    {key: "pending", label: "В процессе", tone: "warn"},
    {key: "overdue", label: "Просрочено", tone: "bad"},
    {key: "closed", label: "Закрыто", tone: "ok"},
    {key: "closedEarly", label: "Досрочно", tone: "ok"},
    {key: "returned", label: "Возврат", tone: "neutral"},
];

export const STATUS_COLORS: Record<StatusKey, string> = {
    new: "hsl(210 90% 55%)",
    pending: "hsl(48 96% 53%)",
    overdue: "hsl(0 90% 55%)",
    closed: "hsl(142 72% 45%)",
    closedEarly: "hsl(125 80% 40%)",
    returned: "hsl(270 90% 60%)",
    unknown: "hsl(220 10% 50%)",
};

function formatCurrency(value: number): string {
    const v = Number(value || 0);
    return new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "KGS",
        minimumFractionDigits: 0,
    }).format(v);
}

function moneyTick(v: any) {
    const n = Number(v || 0);
    if (!Number.isFinite(n)) return "0";
    if (Math.abs(n) >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
    if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
    return `${Math.round(n)}`;
}

/* =========================
   Helpers под твою модель
========================= */

function getCreatedAt(p: PersonModelInterface): Date | null {
    const raw = p?.createdAt;
    if (!raw) return null;
    return raw;
}

function getRatePercent(p: PersonModelInterface): number {
    const n = Number(p?.creditDetail?.percent ?? 0);
    return Number.isFinite(n) ? n : 0;
}

function getNetProfit(p: PersonModelInterface): number {
    const n = Number(p?.creditDetail?.netProfit ?? 0);
    return Number.isFinite(n) ? n : 0;
}

function getIssuedTotal(p: PersonModelInterface): number {
    // total по договору
    const n = Number(p?.creditDetail?.total ?? 0);
    return Number.isFinite(n) ? n : 0;
}

function getDebt(p: PersonModelInterface): number {
    // если remainingDebt есть, берем его, иначе fallback на total
    const rd = Number(p?.creditDetail?.remainingDebt);
    if (Number.isFinite(rd)) return rd;
    return getIssuedTotal(p);
}

function computeMonthlyPlannedAndPaid(people: PersonModelInterface[], baseDate: dayjs.Dayjs) {
    const monthStart = baseDate.startOf("day").locale("ru");
    const currentMonth = monthStart.month();
    const currentYear = monthStart.year();

    let planned = 0;
    let paid = 0;
    let totalClients = 0;
    let paidClients = 0;

    const clients: {
        id: string;
        name: string;
        status: StatusKey;
        planned: number;
        paid: number;
    }[] = [];

    for (const p of people) {
        const status = getStatusKey(p);
        if (status !== "new" && status !== "pending") continue;
        totalClients += 1;

        const mp = Number(p?.creditDetail?.monthlyPayment ?? 0);
        if (!Number.isFinite(mp) || mp <= 0) continue;

        const release = dayjs(p?.creditDetail?.releaseDay);
        if (!release.isValid()) continue;

        const term = Number(p?.creditDetail?.term ?? 0);
        if (!Number.isFinite(term) || term <= 0) continue;

        const monthsPassed = monthStart.diff(release.startOf("day"), "month");
        if (monthsPassed < 0 || monthsPassed >= term) continue;

        const debt = getDebt(p);
        const clientPlanned = Math.max(0, Math.min(mp, debt));

        const clientPaid = (p.payments || [])
        .filter((pay) => pay?.type !== "discount")
        .reduce((sum, pay) => {
            const d = dayjs(pay.date);
            if (d.isValid() && d.month() === currentMonth && d.year() === currentYear) {
                return sum + Number(pay.amount || 0);
            }
            return sum;
        }, 0);

        planned += clientPlanned;
        paid += clientPaid;

        if (clientPaid > 0) {
            paidClients += 1;
        }

        clients.push({
            id: p.id,
            name: p.name,
            status,
            planned: clientPlanned,
            paid: clientPaid,
        });
    }

    const percent = planned > 0 ? Math.min(100, (paid / planned) * 100) : 0;
    const monthLabel = monthStart.format("MMMM YYYY"); // например: "март 2026"

    return {planned, paid, percent, monthLabel, clients, totalClients, paidClients};
}

function MonthlyPaymentsProgress({
    planned,
    paid,
    percent,
    totalClients,
    paidClients,
}: {
    planned: number;
    paid: number;
    percent: number;
    totalClients: number;
    paidClients: number;
}) {
    const displayPercent = Math.round(percent);

    const circleStyle = {
        background: `conic-gradient(hsl(var(--chart-1)) ${percent}%, hsl(var(--muted)) ${percent}% 100%)`,
    } as React.CSSProperties;

    return (
        <div className="flex items-center gap-4">
            <div className="relative h-24 w-24">
                <div className="h-full w-full rounded-full" style={circleStyle}/>
                <div
                    className="absolute inset-2 rounded-full bg-background flex flex-col items-center justify-center text-xs">
                    <span className="text-[11px] text-muted-foreground">Выполнено</span>
                    <span className="text-lg font-semibold">{displayPercent}%</span>
                </div>
            </div>
            <div className="space-y-1 text-sm">
                <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">План за месяц</span>
                    <span className="font-medium">{formatCurrency(planned)}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Оплачено</span>
                    <span className="font-medium">{formatCurrency(paid)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
                    <span>Клиентов оплатили</span>
                    <span className="font-medium">
                        {paidClients} из {totalClients}
                    </span>
                </div>
            </div>
        </div>
    );
}

function getStatusKey(p: PersonModelInterface): StatusKey {
    const s = (p?.status ?? "unknown").toString().toLowerCase();

    // если в базе статусы уже "new/pending/overdue/closed..."
    if (s === "new") return "new";
    if (s === "pending") return "pending";
    if (s === "overdue") return "overdue";
    if (s === "closed") return "closed";
    if (s === "closedearly" || s === "closed_early") return "closedEarly";
    if (s === "returned") return "returned";

    // русские варианты (важен порядок: "досрочно" тоже содержит "закры")
    if (s.includes("проср") || s.includes("overdue") || s.includes("late")) return "overdue";
    if (s.includes("доср") || s.includes("early")) return "closedEarly";
    if (s.includes("закры") || s.includes("closed") || s.includes("completed")) return "closed";
    if (s.includes("в процессе") || s.includes("process") || s.includes("pending")) return "pending";
    if (s.includes("возврат") || s.includes("returned")) return "returned";
    if (s.includes("нов") || s.includes("new")) return "new";

    // если долг 0, считаем закрытым даже если статус не обновили
    if (getDebt(p) <= 0) return "closed";

    return "unknown";
}

function parsePaymentDate(pay: Payment): Date | null {
    // Payment.date: string. Держим ISO или "YYYY-MM-DD"
    const d = dayjs(pay?.date);
    return d.isValid() ? d.toDate() : null;
}

function computeNextScheduledPaymentDate(p: PersonModelInterface): Date | null {
    const release = dayjs(p?.creditDetail?.releaseDay);
    if (!release.isValid()) return null;

    const term = Number(p?.creditDetail?.term ?? 0);
    if (!Number.isFinite(term) || term <= 0) return null;

    const closingTs = p?.creditDetail?.closingDay;
    const closing = closingTs?.toDate ? dayjs(closingTs.toDate()) : dayjs(closingTs);
    const hasClosing = closing.isValid();

    const today = dayjs().startOf("day");

    // если уже закрыт или долга нет
    if (getDebt(p) <= 0) return null;

    // берем текущий номер месяца оплаты
    const monthsPassed = today.diff(release.startOf("day"), "month");
    const nextIndex = Math.max(0, monthsPassed + 1);

    if (nextIndex >= term) return null;

    let next = release.add(nextIndex, "month").startOf("day");

    // если есть closingDay и он раньше next, значит платежей больше не надо
    if (hasClosing && closing.isBefore(next, "day")) return null;

    return next.toDate();
}

function getNextPayment(p: PersonModelInterface): { date: Date | null; amount: number; overdue: boolean } {
    const today = dayjs().startOf("day");

    // 1) если payments содержит будущие даты, берем ближайшую
    const future = (p.payments || [])
        .filter((x) => x?.type !== "discount")
        .map((x) => ({...x, d: parsePaymentDate(x)}))
        .filter((x) => {
            if (!x.d) return false;
            const dd = dayjs(x.d).startOf("day");
            // dd >= today
            return dd.diff(today, "day") >= 0;
        })
        .sort((a, b) => Number(a.d) - Number(b.d));

    if (future.length) {
        const date = future[0].d!;
        const amount = Number(future[0].amount || 0);
        return {date, amount: Number.isFinite(amount) ? amount : 0, overdue: false};
    }

    const date = computeNextScheduledPaymentDate(p);
    const amount = Number(p?.creditDetail?.monthlyPayment ?? 0);
    const safeAmount = Number.isFinite(amount) ? amount : 0;

    const overdue = date ? dayjs(date).startOf("day").diff(today, "day") < 0 : false;

    return {date, amount: safeAmount, overdue};
}

export default function Dashboard() {
    const navigate = useNavigate();

    const {stats} = useClients();
    const {getPeople, people, totalPayments} = usePeople();

    const now = dayjs();
    const [selectedYear, setSelectedYear] = useState(now.year());
    const [selectedMonthIndex, setSelectedMonthIndex] = useState(now.month()); // 0-11


    useEffect(() => {
        getPeople({dateRange: undefined});
    }, [getPeople]);

    const paymentsSum = useMemo(() => {
        const list = (people || []) as PersonModelInterface[];
        return list.reduce((sum, p) => sum + Number(p?.creditDetail?.netProfit || 0), 0);
    }, [people]);

    const monthlyProgress = useMemo(() => {
        const list = (people || []) as PersonModelInterface[];
        const base = dayjs()
            .year(selectedYear)
            .month(selectedMonthIndex)
            .startOf("month");
        const result = computeMonthlyPlannedAndPaid(list, base);

        return result;
    }, [people, selectedYear, selectedMonthIndex]);

    const kpis = useMemo(() => {
        const list = (people || []) as PersonModelInterface[];

        const totalIssued = list.reduce((sum, p) => sum + getIssuedTotal(p), 0);
        const totalProfit = list.reduce((sum, p) => sum + getNetProfit(p), 0);

        const statusCounts: Record<StatusKey, number> = {
            new: 0,
            pending: 0,
            overdue: 0,
            closed: 0,
            closedEarly: 0,
            returned: 0,
            unknown: 0,
        };

        for (const p of list) statusCounts[getStatusKey(p)] += 1;

        const completedCount = statusCounts.closed + statusCounts.closedEarly;
        const totalCount = Math.max(1, list.length);
        const successRate = (completedCount / totalCount) * 100;

        const rates = list.map(getRatePercent).filter((n) => Number.isFinite(n) && n > 0);
        const avgRate = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;

        return {
            totalIssued,
            totalProfit,
            avgRate,
            completedCount,
            successRate,
            statusCounts,
        };
    }, [people]);

    const monthlyIncomeRows = useMemo(() => {
        const list = (people || []) as PersonModelInterface[];
        const map = new Map<string, { income: number; expenses: number }>();

        for (const p of list) {
            const d = getCreatedAt(p);
            if (!d) continue;

            const key = dayjs(d).format("YYYY-MM");
            const item = map.get(key) || {income: 0, expenses: 0};

            item.income += getNetProfit(p);

            // твоя себестоимость лежит в phone.costPrice
            const exp = Number(p?.phone?.costPrice ?? 0);
            item.expenses += Number.isFinite(exp) ? exp : 0;

            map.set(key, item);
        }

        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([ym, v]) => ({
                month: dayjs(ym + "-01").format("MMM YY"),
                income: v.income,
                expenses: v.expenses,
            }));
    }, [people]);

    const statusDistribution = useMemo(() => {
        const rows = STATUS_META.map((s) => ({
            key: s.key,
            name: s.label,
            value: kpis.statusCounts[s.key] || 0,
        })).filter((x) => x.value > 0);

        if (!rows.length) {
            const u = kpis.statusCounts.unknown || 0;
            return u ? [{key: "unknown" as const, name: "Другое", value: u}] : [];
        }
        return rows;
    }, [kpis.statusCounts]);

    const recentActivities = useMemo(() => {
        const list = ((people || []) as PersonModelInterface[])
            .map((p) => {
                const created = getCreatedAt(p);
                return {
                    id: p.id,
                    client: p.name || "Клиент",
                    amount: getIssuedTotal(p),
                    createdAt: created,
                    status: getStatusKey(p),
                };
            })
            .filter((x) => x.createdAt)
            .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
            .slice(0, 7);

        return list;
    }, [people]);

    const upcomingPayments = useMemo(() => {
        const list = ((people || []) as PersonModelInterface[])
            .map((p) => {
                const next = getNextPayment(p);
                return {
                    id: p.id,
                    client: p.name || "Клиент",
                    date: next.date,
                    amount: next.amount,
                    overdue: next.overdue || getStatusKey(p) === "overdue",
                };
            })
            .filter((x) => x.date && x.amount > 0)
            .sort((a, b) => Number(a.date) - Number(b.date))
            .slice(0, 6);

        return list;
    }, [people]);

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Главная</h1>
                    <p className="text-muted-foreground">Обзор вашей CRM системы</p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => navigate("/calculator")}>
                        Калькулятор
                    </Button>
                    <Button onClick={() => navigate("/people")} size="lg">
                        Добавить клиента
                    </Button>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title="Общий долг"
                    value={formatCurrency(stats.totalDebt)}
                    icon={DollarSign}
                    description="Сумма активных кредитов"
                />
                <StatCard
                    title="Активные клиенты"
                    value={stats.activeCount}
                    icon={Users}
                    description="Клиентов в работе"
                />
                <StatCard
                    title="Просрочено"
                    value={stats.overdueCount}
                    icon={AlertTriangle}
                    description="Клиентов с просрочкой"
                    className={stats.overdueCount > 0 ? "border-destructive/50" : ""}
                />
                <StatCard
                    title="Выплаты сегодня"
                    value={formatCurrency(stats.todayPayments)}
                    icon={Calendar}
                    description="Ожидаемые платежи"
                />
            </div>

            <Card className="overflow-hidden">
                <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <CardTitle>Выплаты за месяц</CardTitle>
                            <CardDescription>
                                План и факт по активным договорам за{" "}
                                {monthlyProgress.monthLabel.toLowerCase()}
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-[110px]">
                                <Select
                                    value={String(selectedYear)}
                                    onValueChange={(val) => setSelectedYear(Number(val))}
                                >
                                    <SelectTrigger className="h-8 px-2 text-xs">
                                        <SelectValue placeholder="Год"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 7}).map((_, idx) => {
                                            const year = dayjs().year() - 1 + idx; // прошлый, текущий и ещё 5 лет вперёд
                                            return (
                                                <SelectItem key={year} value={String(year)}>
                                                    {year}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="w-[120px]">
                                <Select
                                    value={String(selectedMonthIndex)}
                                    onValueChange={(val) => setSelectedMonthIndex(Number(val))}
                                >
                                    <SelectTrigger className="h-8 px-2 text-xs">
                                        <SelectValue placeholder="Месяц"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 12}).map((_, idx) => {
                                            const d = dayjs().month(idx).locale("ru");
                                            const label = d.format("MMMM");
                                            return (
                                                <SelectItem key={idx} value={String(idx)}>
                                                    {label}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <MonthlyPaymentsProgress
                        planned={monthlyProgress.planned}
                        paid={monthlyProgress.paid}
                        percent={monthlyProgress.percent}
                        totalClients={monthlyProgress.totalClients}
                        paidClients={monthlyProgress.paidClients}
                    />
                </CardContent>
            </Card>

            <div className="grid gap-4 md:grid-cols-4">
                <Card className="overflow-hidden">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <Target className="h-4 w-4"/>
                            <span className="text-xs">Всего выдано</span>
                        </div>
                        <p className="text-xl font-semibold">{formatCurrency(kpis.totalIssued)}</p>
                    </CardContent>
                </Card>

                <Card className="overflow-hidden">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <Percent className="h-4 w-4"/>
                            <span className="text-xs">Средняя ставка</span>
                        </div>
                        <p className="text-xl font-semibold">{kpis.avgRate ? `${kpis.avgRate.toFixed(1)}%` : "0%"}</p>
                    </CardContent>
                </Card>

                <Card className="overflow-hidden">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <TrendingUp className="h-4 w-4"/>
                            <span className="text-xs">Успешность</span>
                        </div>
                        <p className="text-xl font-semibold">{kpis.successRate.toFixed(0)}%</p>
                    </CardContent>
                </Card>

                <Card className="overflow-hidden">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <Clock className="h-4 w-4"/>
                            <span className="text-xs">Завершено</span>
                        </div>
                        <p className="text-xl font-semibold">{kpis.completedCount} договоров</p>
                    </CardContent>
                </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                <Card className="lg:col-span-2 overflow-hidden">
                    <CardHeader>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <CardTitle>Доходы и расходы</CardTitle>
                                <CardDescription>Динамика по месяцам за все время</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="gap-1">
                                    <Wallet className="h-3 w-3"/>
                                    {formatNumber(paymentsSum || 0)}
                                </Badge>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={monthlyIncomeRows}>
                                    <defs>
                                        <linearGradient id="dashIncomeGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.32}/>
                                            <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="dashExpensesGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="hsl(var(--chart-3))" stopOpacity={0.28}/>
                                            <stop offset="95%" stopColor="hsl(var(--chart-3))" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>

                                    <CartesianGrid strokeDasharray="3 3" className="stroke-border"/>
                                    <XAxis dataKey="month" className="text-xs fill-muted-foreground" tickLine={false}
                                           axisLine={false}/>
                                    <YAxis className="text-xs fill-muted-foreground" tickLine={false} axisLine={false}
                                           tickFormatter={moneyTick}/>

                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: "hsl(var(--popover))",
                                            border: "1px solid hsl(var(--border))",
                                            borderRadius: "var(--radius)",
                                            color: "hsl(var(--popover-foreground))",
                                        }}
                                        formatter={(value: any, _name: any, props: any) => [String(value ?? 0), props?.payload?.name ?? ""]}

                                    />
                                    <Legend/>

                                    <Area type="monotone" dataKey="income" stroke="hsl(var(--chart-1))" strokeWidth={2}
                                          fill="url(#dashIncomeGradient)" name="Доход"/>
                                    <Area type="monotone" dataKey="expenses" stroke="hsl(var(--chart-3))"
                                          strokeWidth={2} fill="url(#dashExpensesGradient)" name="Расходы"/>
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                <Card className="overflow-hidden">
                    <CardHeader>
                        <CardTitle>Статусы кредитов</CardTitle>
                        <CardDescription>Распределение по статусам</CardDescription>
                    </CardHeader>

                    <CardContent>
                        <div className="h-[220px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={statusDistribution} cx="50%" cy="50%" innerRadius={52} outerRadius={86}
                                         paddingAngle={4} dataKey="value">
                                        {statusDistribution.map((entry) => (
                                            <Cell key={entry.key}
                                                  fill={STATUS_COLORS[entry.key as StatusKey] ?? STATUS_COLORS.unknown}/>
                                        ))}
                                    </Pie>

                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: "hsl(var(--popover))",
                                            border: "1px solid hsl(var(--border))",
                                            borderRadius: "var(--radius)",
                                            color: "hsl(var(--popover-foreground))",
                                        }}
                                        formatter={(value: any, _name: any, props: any) => [String(value ?? 0), props?.payload?.name ?? ""]}

                                    />
                                    <Legend/>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="mt-3 grid gap-2">
                            {STATUS_META.map((s) => {
                                const val = kpis.statusCounts[s.key] || 0;
                                if (!val) return null;
                                return (
                                    <div key={s.key}
                                         className="flex items-center justify-between rounded-md border px-3 py-2">
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="h-2.5 w-2.5 rounded-full"
                                                  style={{background: STATUS_COLORS[s.key]}}/>
                                            {s.label}
                                        </div>
                                        <Badge
                                            variant={s.tone === "bad" ? "destructive" : s.tone === "warn" ? "secondary" : "outline"}>{val}</Badge>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                <Card className="lg:col-span-2 overflow-hidden">
                    <CardHeader>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <CardTitle>Последние операции</CardTitle>
                                <CardDescription>Недавние добавления по договорам</CardDescription>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => navigate("/people")}>
                                Все клиенты
                            </Button>
                        </div>
                    </CardHeader>

                    <CardContent>
                        <div className="space-y-3">
                            {recentActivities.length ? (
                                recentActivities.map((a) => {
                                    const label = STATUS_META.find((x) => x.key === a.status)?.label ?? "Другое";
                                    const color = STATUS_COLORS[a.status] ?? STATUS_COLORS.unknown;

                                    return (
                                        <div key={a.id}
                                             className="flex items-center justify-between rounded-lg border p-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="h-9 w-9 rounded-full flex items-center justify-center"
                                                     style={{background: `${color}20`, color}}>
                                                    {a.status === "overdue" ? <AlertTriangle
                                                        className="h-4 w-4"/> : a.status === "closed" || a.status === "closedEarly" ?
                                                        <TrendingUp className="h-4 w-4"/> :
                                                        <Users className="h-4 w-4"/>}
                                                </div>

                                                <div className="min-w-0">
                                                    <p className="font-medium truncate">{a.client}</p>
                                                    <p className="text-sm text-muted-foreground">{a.createdAt ? dayjs(a.createdAt).format("DD.MM.YYYY HH:mm") : ""}</p>
                                                </div>
                                            </div>

                                            <div className="text-right">
                                                <p className="font-semibold">{formatCurrency(a.amount)}</p>
                                                <Badge
                                                    variant={a.status === "overdue" ? "destructive" : a.status === "pending" ? "secondary" : "outline"}
                                                    className="text-xs">
                                                    {label}
                                                </Badge>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">Нет
                                    активности</div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="overflow-hidden">
                    <CardHeader>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <CardTitle>Ближайшие платежи</CardTitle>
                                <CardDescription>Следующие даты по графику</CardDescription>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => navigate("/analytics")}>
                                Аналитика
                            </Button>
                        </div>
                    </CardHeader>

                    <CardContent>
                        <div className="space-y-3">
                            {upcomingPayments.length ? (
                                upcomingPayments.map((p) => (
                                    <div key={p.id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div
                                                className={`h-2.5 w-2.5 rounded-full ${p.overdue ? "bg-destructive" : "bg-chart-1"}`}/>
                                            <div className="min-w-0">
                                                <p className="font-medium truncate">{p.client}</p>
                                                <p className="text-sm text-muted-foreground">{p.date ? dayjs(p.date).locale("ru").format("DD MMM") : ""}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={`font-semibold ${p.overdue ? "text-destructive" : ""}`}>{formatCurrency(p.amount)}</p>
                                            {p.overdue ? <Badge variant="destructive"
                                                                className="text-xs">Просрочено</Badge> : null}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
                                    Нет запланированных платежей
                                </div>
                            )}
                        </div>

                        <Button variant="outline" className="w-full mt-4" onClick={() => navigate("/people")}>
                            Все платежи
                        </Button>
                    </CardContent>
                </Card>
            </div>

            <Card className="overflow-hidden">
                <CardHeader>
                    <CardTitle>Быстрые действия</CardTitle>
                    <CardDescription>Частые операции</CardDescription>
                </CardHeader>

                <CardContent className="flex flex-wrap gap-3">
                    <Button variant="outline" className="h-12" onClick={() => navigate("/people")}>
                        <Users className="h-4 w-4 mr-2"/>
                        Клиенты
                    </Button>
                    <Button variant="outline" className="h-12" onClick={() => navigate("/calculator")}>
                        <DollarSign className="h-4 w-4 mr-2"/>
                        Калькулятор
                    </Button>
                    <Button variant="outline" className="h-12" onClick={() => navigate("/analytics")}>
                        <TrendingUp className="h-4 w-4 mr-2"/>
                        Аналитика
                    </Button>
                    <Button variant="outline" className="h-12" onClick={() => navigate("/settings")}>
                        <Calendar className="h-4 w-4 mr-2"/>
                        Настройки
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}
